'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import type { EmblaCarouselType, EmblaOptionsType } from 'embla-carousel';
import Autoplay from 'embla-carousel-autoplay';
import {
  DEFAULT_AUTOPLAY_DELAY,
  prefersReducedMotion,
  resolveLabels,
  resolveMotionConfig,
} from './config';
import { getSlideId } from './get-slide-id';
import { getTabId } from './get-tab-id';
import {
  EMBLA_AUTOPLAY_EVENTS,
  EMBLA_NAV_EVENTS,
  readCanScrollNext,
  readCanScrollPrev,
  readIsAutoplayPlaying,
  readSelectedSnap,
  useEmblaSnapshot,
} from './use-embla-snapshot';
import { CarouselLabels } from './carousel-labels';

export interface UseHeroCarouselOptions {
  /** Number of slides. Provided explicitly so dots render correctly during SSR. */
  slideCount: number;
  loop?: boolean;
  autoplayDelay?: number;
  labels?: CarouselLabels;
}

export interface HeroCarouselApi {
  viewportRef: (node: HTMLElement | null) => void;
  emblaApi: EmblaCarouselType | undefined;
  slideCount: number;
  canPrev: boolean;
  canNext: boolean;
  selectedIndex: number;
  scrollPrev: () => void;
  scrollNext: () => void;
  scrollTo: (index: number) => void;
  labels: Required<CarouselLabels>;
  getSlideId: typeof getSlideId;
  getTabId: typeof getTabId;
  hasAutoplay: boolean;
  isAutoplayPlaying: boolean;
  toggleAutoplay: () => void;
}

/**
 * Headless carousel behaviour. Owns Embla setup, autoplay, the
 * `prefers-reduced-motion` branch, navigation state and a11y id helpers — but
 * renders nothing. Each portal builds its own markup around the returned API,
 * either directly or through the compound `<Carousel>` primitives.
 */
export function useHeroCarousel({
  slideCount,
  loop = true,
  autoplayDelay = DEFAULT_AUTOPLAY_DELAY,
  labels,
}: UseHeroCarouselOptions): HeroCarouselApi {
  // Read the motion preference once at mount; both plugins and Embla options
  // depend on it and must be decided before the carousel initialises.
  const [motion] = useState(() => resolveMotionConfig(prefersReducedMotion()));

  const [plugins] = useState(() =>
    motion.autoplay
      ? [
          Autoplay({
            playOnInit: true,
            delay: autoplayDelay,
            stopOnMouseEnter: true,
            stopOnInteraction: false,
          }),
        ]
      : [],
  );

  const [emblaOptions] = useState<EmblaOptionsType>(() => ({
    loop,
    axis: 'x',
    align: 'start',
    duration: motion.duration,
  }));

  const [viewportRef, emblaApi] = useEmblaCarousel(emblaOptions, plugins);

  const canPrev = useEmblaSnapshot(emblaApi, EMBLA_NAV_EVENTS, readCanScrollPrev, false);
  const canNext = useEmblaSnapshot(emblaApi, EMBLA_NAV_EVENTS, readCanScrollNext, false);
  const selectedIndex = useEmblaSnapshot(emblaApi, EMBLA_NAV_EVENTS, readSelectedSnap, 0);
  const isAutoplayPlaying = useEmblaSnapshot(
    emblaApi,
    EMBLA_AUTOPLAY_EVENTS,
    readIsAutoplayPlaying,
    motion.autoplay,
  );

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((index: number) => emblaApi?.scrollTo(index), [emblaApi]);

  const userPausedAutoplayRef = useRef(false);
  const toggleAutoplay = useCallback(() => {
    const autoplay = emblaApi?.plugins().autoplay;
    if (!autoplay) return;
    if (autoplay.isPlaying()) {
      userPausedAutoplayRef.current = true;
      autoplay.stop();
    } else {
      userPausedAutoplayRef.current = false;
      autoplay.play();
    }
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const vetoAutoResume = () => {
      if (!userPausedAutoplayRef.current) return;
      queueMicrotask(() => emblaApi.plugins().autoplay?.stop());
    };
    emblaApi.on('autoplay:play', vetoAutoResume);
    return () => {
      emblaApi.off('autoplay:play', vetoAutoResume);
    };
  }, [emblaApi]);

  const resolvedLabels = useMemo(() => resolveLabels(labels), [labels]);

  return {
    viewportRef,
    emblaApi,
    slideCount,
    canPrev,
    canNext,
    selectedIndex,
    scrollPrev,
    scrollNext,
    scrollTo,
    labels: resolvedLabels,
    getSlideId,
    getTabId,
    hasAutoplay: motion.autoplay,
    isAutoplayPlaying,
    toggleAutoplay,
  };
}
